import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Lock, User, ArrowLeft, Chrome } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ecaa2978-2f20-40e3-8453-4248c3146dce/artifacts/mfi4q0wb_7710DE4F-B183-47BF-A0BF-17D901748F49.png";

const CURRENCIES = [
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "CAD", symbol: "$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "$", name: "Australian Dollar" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" }
];

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    currency: "GBP"
  });
  
  const navigate = useNavigate();
  const { login, user } = useAuth();

  useEffect(() => {
    if (user) {
      // Redirect based on onboarding completion status
      if (user.onboarding_completed) {
        navigate('/calendar');
      } else {
        navigate('/onboarding');
      }
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      
      login(response.data.user, response.data.token);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      
      // Redirect based on onboarding completion status
      if (response.data.user?.onboarding_completed) {
        navigate('/calendar');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/calendar';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-p2p-bg flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-p2p-teal">
        <div className="absolute inset-0 bg-gradient-to-br from-p2p-teal-light/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center p-12 xl:p-20">
          <img src={LOGO_URL} alt="Path2Plenty" className="h-24 w-auto mb-8" />
          <h1 className="font-manrope text-4xl xl:text-5xl font-bold text-white mb-6">
            Your path to financial peace of mind
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Track your bills, manage debt, and build a brighter financial future with Path2Plenty.
          </p>
          
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold">1</span>
              </div>
              <p className="text-white">Connect your bills and income</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold">2</span>
              </div>
              <p className="text-white">See your cash flow at a glance</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold">3</span>
              </div>
              <p className="text-white">Get personalized insights</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Button 
            variant="ghost" 
            className="mb-8 text-p2p-muted hover:text-p2p-teal"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to home
          </Button>

          <div className="light-card p-8 rounded-2xl">
            <div className="lg:hidden flex justify-center mb-6">
              <img src={LOGO_URL} alt="Path2Plenty" className="h-16 w-auto" />
            </div>

            <h2 className="font-manrope text-2xl font-bold text-p2p-teal mb-2">
              {isLogin ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="text-p2p-muted mb-6">
              {isLogin ? 'Sign in to continue your journey' : 'Start your path to financial freedom'}
            </p>

            {/* Google Sign In */}
            <Button 
              variant="outline" 
              className="w-full mb-6 border-p2p-border text-p2p-teal hover:bg-p2p-teal/5 h-12"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <Chrome className="w-5 h-5 mr-2" />
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-p2p-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-p2p-muted">or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-p2p-teal font-medium">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-p2p-muted" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-light h-12 pl-10"
                      required={!isLogin}
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-p2p-teal font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-p2p-muted" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-light h-12 pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-p2p-teal font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-p2p-muted" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-light h-12 pl-10"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="currency" className="text-p2p-teal font-medium">Preferred Currency</Label>
                  <Select value={formData.currency} onValueChange={(val) => setFormData(prev => ({ ...prev, currency: val }))}>
                    <SelectTrigger className="input-light h-12" data-testid="currency-select">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-p2p-border">
                      {CURRENCIES.map(curr => (
                        <SelectItem 
                          key={curr.code} 
                          value={curr.code}
                          className="text-p2p-teal hover:bg-p2p-teal/5"
                        >
                          {curr.symbol} {curr.name} ({curr.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full btn-gold h-12 rounded-xl mt-6"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            <p className="text-center text-p2p-muted mt-6">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                className="text-p2p-gold hover:underline font-medium"
                onClick={() => setIsLogin(!isLogin)}
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
